import { FC, memo } from 'react';

import { ModalProps, ModalTypes } from 'application/providers/ModalProvider';
import { RouterUtils, useLocation, useNavigate } from 'application/providers/RouterProvider';
import { LocationStatePointsType } from 'application/pages/types';
import { POINTS_MENU_PATH, POINTS_PAGE_PATH, STREAMS_CATEGORY_PATH, VIDEO_STREAMS_MENU_PATH } from 'application/consts';
import ConnectWithManagerButton from 'application/components/ConnectWithManagerButton';
import { useAccountCompanyCountry, useAccountCompanyDeviceRequests } from 'application/hooks';

import ModalScreen from '@zvuk-b2b/react-uikit/ui/ModalScreen';
import IconMapPin from '@zvuk-b2b/react-uikit/ui/icons/MapPin';
import Button, { ButtonType } from '@zvuk-b2b/react-uikit/ui/Button';

import { CompanyDeviceRequestPureType } from 'domain/api/graphql/generated';

import useDeviceBindSuccessModalViewModel from './useDeviceBindSuccessModalViewModel';
import { useOnboardingContext } from '../useOnboardingContext';
import { useOnboardingActionContext } from '../useOnboardingActionContext';
import { TEXTS } from './texts';
import { ModalDescription, SuccessActionButton } from './components';
import { CHANGE_DEVICE_FLOWS, VIDEO_FLOWS } from '../consts';

import styles from './index.module.scss';
import { PlayerTypesInFlow, SelectPlayerContentViewMode, SelectPlayerFlowType } from '../types';

const getModalTitle = (isChangeDeviceFlow: boolean, isVideoDemo: boolean, deviceRequestsByPointCount: number) => {
  if ((!isChangeDeviceFlow || isVideoDemo) && deviceRequestsByPointCount !== 0) {
    return TEXTS.MODAL_BIND_SUCCESS_TITLE_ON_REQUESTED;
  }

  return TEXTS.MODAL_BIND_SUCCESS_TITLE;
};

const DeviceBindSuccessModal: FC<ModalProps[ModalTypes.DEVICE_BIND_SUCCESS]> = ({ close, replaceModal }) => {
  const { flowType, currentPoint, contentViewMode, selectedPlayerInFlow } = useOnboardingContext();
  const { clearCurrentPoint, setFlowType } = useOnboardingActionContext();

  const companyCountry = useAccountCompanyCountry();
  const deviceRequests = useAccountCompanyDeviceRequests();

  const navigate = useNavigate();
  const location = useLocation<LocationStatePointsType>();

  const isChangeDeviceFlow = Boolean(flowType && CHANGE_DEVICE_FLOWS.has(flowType));
  const isVideoStreamMode = contentViewMode === SelectPlayerContentViewMode.VIDEO_STREAM;

  const deviceRequestsByPoint = deviceRequests.reduce((acc, request) => {
    if (request.pointId === currentPoint.id) {
      acc.push(request);
    }

    return acc;
  }, [] as CompanyDeviceRequestPureType[]);

  const isShowBindStreamInfo = (!isChangeDeviceFlow || currentPoint?.isVideoDemo) && deviceRequestsByPoint.length !== 0;

  const {
    addressHasStream,
    addressHasVideoStream,
    showWebPlayerButton,
    onOpenWebPlayerClick,
    onChangeStreamClick,
    onChangeVideoStreamClick,
    onClose,
    onBack,
  } = useDeviceBindSuccessModalViewModel({
    currentPoint,
    flowType,
    pathname: location.pathname,
    redirectToPointsPage: () => {
      navigate(RouterUtils.generatePath(POINTS_MENU_PATH));
    },
    redirectToPoint: () => {
      navigate(RouterUtils.generatePath(POINTS_PAGE_PATH, { id: currentPoint?.id }), { replace: true });
    },
    redirectToStreamsPage: () => {
      navigate(
        RouterUtils.generatePath(STREAMS_CATEGORY_PATH, {
          category: 'recommended',
        }),
        {
          state: { pointsFromLocation: [{ pointId: currentPoint?.id, pointAddress: currentPoint?.address }] },
        }
      );
    },
    redirectToVideoStreamsPage: () => {
      navigate(RouterUtils.generatePath(VIDEO_STREAMS_MENU_PATH), {
        state: { pointsFromLocation: [{ pointId: currentPoint?.id, pointAddress: currentPoint?.address }] },
      });
    },
    close,
    replaceModal,
    clearCurrentPoint,
    setFlowType,
  });

  const showStreamsButton = !isVideoStreamMode && !addressHasStream;

  const showVideoStreamsButton =
    isVideoStreamMode &&
    !addressHasVideoStream &&
    flowType !== SelectPlayerFlowType.CREATE_DEMO_POINT &&
    Boolean(currentPoint?.isVideoMode);

  const isBindVideoStreamFlow =
    Boolean(flowType && VIDEO_FLOWS.has(flowType)) ||
    (contentViewMode === SelectPlayerContentViewMode.VIDEO_STREAM && Boolean(currentPoint?.isVideoMode)) ||
    selectedPlayerInFlow === PlayerTypesInFlow.VIDEOPLAYER;

  return (
    <ModalScreen
      title={getModalTitle(isChangeDeviceFlow, Boolean(currentPoint?.isVideoDemo), deviceRequestsByPoint.length)}
      titlePlacement="left"
      containerClassName={styles.container}
      onClose={isShowBindStreamInfo ? onBack : onClose}
      actionButtons={
        isShowBindStreamInfo
          ? [
              <ConnectWithManagerButton
                key="button_to_manager_device_bind_success_modal"
                country={companyCountry}
              />,
              <Button
                key="button_cancel_bind_success_modal"
                elementId="button_cancel_bind_success_modal"
                type={ButtonType.SECONDARY}
                onClick={onBack}>
                {TEXTS.MODAL_BIND_SUCCESS_BUTTON_TO_STREAMS_TEXT(isVideoStreamMode)}
              </Button>,
            ]
          : [
              <SuccessActionButton
                key="button_open_whatever_bind_success_modal"
                showStreamsButton={showStreamsButton}
                showVideoStreamsButton={showVideoStreamsButton}
                showWebPlayerButton={showWebPlayerButton}
                onChangeStreamClick={onChangeStreamClick}
                onChangeVideoStreamClick={onChangeVideoStreamClick}
                onOpenWebPlayerClick={onOpenWebPlayerClick}
                onClose={onClose}
              />,
            ]
      }>
      <ModalDescription
        contentViewMode={contentViewMode}
        showStreamsDescription={showStreamsButton}
        addressHasVideoStream={addressHasVideoStream}
        addressIsVideoDemo={currentPoint?.isVideoDemo}
        isAddressEnabled={currentPoint?.isEnabled === true}
        isChangeDeviceFlow={isChangeDeviceFlow}
        isBindVideoStreamFlow={isBindVideoStreamFlow}
        deviceRequestsByPoint={deviceRequestsByPoint}
        isVideoMode={Boolean(currentPoint.isVideoMode)}
      />

      <div
        key={currentPoint?.id}
        className={styles.address}>
        <IconMapPin className={styles.addressIcon} />
        <span>{currentPoint?.address}</span>
      </div>
    </ModalScreen>
  );
};

export default memo(DeviceBindSuccessModal);
